import moment from "moment";

import { Cell } from "react-table";
import { ReservationListModel } from "src/api/reservation/reservation-types";
import { BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import DetailLink from "src/components/DetailLink";
import Link from "src/components/PartnerLink";
import PhoneNumberColumn from "src/components/PhoneNumberColumn";
import PagePath from "src/pagePath.json";
import { formatPhoneNumber, ViewYmdFormat } from "src/utils";
import { getStatusTextAndColor, inboundChannelType } from "../../reservation-types";

// ===== 공통 포맷터 (Cell / excelValue 양쪽에서 재사용) =====
const getFacilityTypeText = (type: string) => {
  switch (type) {
    case "DESK":
      return "좌석";
    case "MEETING_ROOM":
      return "회의실";
    default:
      return "편의시설";
  }
};

// 예약 경로 표기: inboundChannelType 상수를 단일 소스로 사용 (화면/엑셀 공통)
const getExternalServiceTypeText = (type: string) => {
  return inboundChannelType[type as keyof typeof inboundChannelType] || "알 수 없음";
};

/*
  공용공간 예약 목록 columns
*/

interface ColumnDefinition {
  Header: string;
  // accessor: string | ((item: ReservationListModel) => string);
  accessor: string;
  width?: number;
  sticky?: string;
  align?: "center" | "right"; // BaseTable 정렬(미지정 시 좌측)
  Cell?: (props: Cell<ReservationListModel>) => JSX.Element | string;
  excelValue?: (item: ReservationListModel) => any;
}

const ReservationColumns: ColumnDefinition[] = [
  {
    Header: "예약 ID",
    accessor: "id",
    sticky: "left",
    width: 120,
    Cell: (props: Cell<ReservationListModel>) => {
      const detailPath = `${PagePath.reservation.detail.replace(":id", `${props.row.original.id}`)}`;
      const groupId = props.row.original.parentId;
      return (
        <div>
          <DetailLink className="text-underline" to={`${detailPath}${(groupId && `?groupID=${groupId}`) || ""}`}>
            {props.value}
          </DetailLink>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.id,
  },
  {
    Header: "예약 그룹 ID",
    accessor: "parentId",
    sticky: "left",
    width: 120,
    Cell: (props: Cell<ReservationListModel>) => {
      // 동일한 예약 그룹 ID에 속한 예약 목록을 새 창으로 조회 (공용공간 예약 리스트 > 예약 그룹 ID 필터링)
      const groupListPath = `${PagePath.reservation.list}?parentId=${props.value}`;
      return (
        <div>
          {props.value && props.value !== "0" ? (
            <Link className="text-underline" to={groupListPath} target="_blank">
              {props.value}
            </Link>
          ) : (
            <span>-</span>
          )}
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.parentId || "-",
  },
  {
    Header: "예약 상태",
    accessor: "status.code",
    sticky: "left",
    width: 90,
    align: "center",
    Cell: ({ value, row }: Cell<ReservationListModel>) => {
      const startDate = row.original.start;
      const endDate = row.original.end;
      const textAndColor = getStatusTextAndColor(value, startDate, endDate);
      return <div className={`${textAndColor.color}`}>{textAndColor.label}</div>;
    },
    excelValue: (item: ReservationListModel) => getStatusTextAndColor(item.status.code, item.start, item.end).label,
  },
  {
    Header: "예약 경로",
    accessor: "inboundChannel",
    width: 150,
    align: "center",
    Cell: (props: Cell<ReservationListModel>) => {
      const externalServiceType = getExternalServiceTypeText(props.value);
      return (
        <div>
          <span>{externalServiceType || "-"}</span>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => getExternalServiceTypeText(item.inboundChannel),
  },
  {
    Header: "계약 ID",
    accessor: "contract.id",
    width: 120,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      // 선택 시 해당 계약 상세 페이지로 이동
      if (!value) return "-";
      const detailPath = `${PagePath.contract.detail.replace(":id", `${value}`)}`;
      return <BaseNewTabLink path={detailPath} value={String(value)} className="w-100" />;
    },
    excelValue: (item: ReservationListModel) => item.contract?.id || "-",
  },
  {
    Header: "계약명",
    accessor: "contract.contractName",
    width: 160,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      // 계약 ID 컬럼에서 이미 신청/계약 상세 진입이 가능하여 중복 링크 제거 (단순 텍스트로 노출)
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value || "-"} isSingleLine />
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.contract?.contractName || "-",
  },
  {
    Header: "소속 회사",
    accessor: "contract.mbOrganizationName",
    width: 160,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      return <BaseTooltip contents={value || "-"} isSingleLine className="text-left w-100" />;
    },
    excelValue: (item: ReservationListModel) => item.contract?.mbOrganizationName || "-",
  },
  {
    Header: "건물명",
    accessor: "facility.building.name",
    width: 160,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value || "-"} className="text-left w-100" />
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.facility?.building?.name || "-",
  },
  {
    Header: "예약명",
    accessor: "summary",
    width: 150,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value || "-"} isSingleLine />
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.summary || "-",
  },
  {
    Header: "위치",
    accessor: "facility.floor",
    width: 80,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      const floorText = value > 0 ? "지상" : "지하";
      return (
        <div>
          <span>
            {floorText} {Math.abs(value)}층
          </span>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => {
      const floor = item.facility?.floor;
      if (floor === undefined || floor === null) return "-";
      const floorText = floor > 0 ? "지상" : "지하";
      return `${floorText} ${Math.abs(floor)}층`;
    },
  },
  {
    Header: "공용공간 유형",
    accessor: "facility.type",
    width: 120,
    align: "center",
    Cell: (props: Cell<ReservationListModel>) => {
      if (props.value === "MEETING_ROOM") {
        return <span>회의실</span>;
      } else if (props.value === "DESK") {
        return <span>좌석</span>;
      } else {
        return <span>편의시설</span>;
      }
    },
    excelValue: (item: ReservationListModel) => getFacilityTypeText(item.facility?.type || ""),
  },
  {
    Header: "공용공간명",
    accessor: "facility.name",
    width: 120,
    Cell: ({ row, value }: Cell<ReservationListModel>) => {
      const deskGroupName = row.original.facility.buildingCommonFacilityDeskGroupName;
      return (
        <div className="w-100 text-left">
          {row.original.facility.type === "DESK" ? (
            <BaseTooltip contents={`${deskGroupName ? deskGroupName + " > " + value : value}`} />
          ) : (
            <BaseTooltip contents={value || ""} />
          )}
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => {
      if (item.facility?.type === "DESK") {
        const deskGroupName = item.facility.buildingCommonFacilityDeskGroupName;
        return deskGroupName ? `${deskGroupName} > ${item.facility.name}` : item.facility.name || "-";
      }
      return item.facility?.name || "-";
    },
  },
  {
    Header: "예약 시작일시",
    accessor: "start",
    width: 160,
    align: "center",
    Cell: ({ value }: Cell<ReservationListModel>) => {
      // ISO 8601 표준(YYYY-MM-DD HH:mm:ss), 요일 표기 제거
      return (
        <div>
          <span>{value ? moment(value).format(ViewYmdFormat.FULL) : "-"}</span>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => (item.start ? moment(item.start).format(ViewYmdFormat.FULL) : "-"),
  },
  {
    Header: "예약 종료일시",
    accessor: "end",
    width: 160,
    align: "center",
    Cell: ({ value }: Cell<ReservationListModel>) => {
      // ISO 8601 표준(YYYY-MM-DD HH:mm:ss), 요일 표기 제거
      return (
        <div>
          <span>{value ? moment(value).format(ViewYmdFormat.FULL) : "-"}</span>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => (item.end ? moment(item.end).format(ViewYmdFormat.FULL) : "-"),
  },
  {
    Header: "이용시간",
    accessor: "usingTime",
    width: 120,
    Cell: ({ row }: Cell<ReservationListModel>) => {
      // UTC 시간을 한국 시간으로 변환하고 분까지만 고려
      const start = moment(row.original.start).startOf("minute");
      const end = moment(row.original.end).startOf("minute");

      // 분 단위 차이 계산
      const diffInMinutes = end.diff(start, "minutes");

      const days = Math.floor(diffInMinutes / 1440); // 1일 = 1440분
      const remainingMinutesAfterDays = diffInMinutes % 1440;
      const hours = Math.floor(remainingMinutesAfterDays / 60);
      const minutes = remainingMinutesAfterDays % 60;

      let displayTime = "";

      if (days > 0) {
        displayTime += `${days}일 `;
        if (hours > 0) displayTime += `${hours}시간 `;
        if (minutes > 0) displayTime += `${minutes}분`;
      } else if (hours > 0) {
        displayTime += `${hours}시간`;
        if (minutes > 0) displayTime += ` ${minutes}분`;
      } else {
        displayTime += `${minutes}분`;
      }

      return <div>{displayTime.trim()}</div>;
    },
    excelValue: (item: ReservationListModel) => {
      if (!item.start || !item.end) return "-";
      const start = moment(item.start).startOf("minute");
      const end = moment(item.end).startOf("minute");
      return `${end.diff(start, "minutes")}`;
    },
  },

  {
    Header: "주최자 회원번호",
    accessor: "organizer.memberNo",
    width: 120,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      return (
        <div className="d-flex flex-column">
          <span>{value || "-"}</span>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.organizer?.memberNo || "-",
  },
  {
    Header: "주최자 휴대폰번호",
    accessor: "organizer.phoneNumber",
    width: 120,
    align: "center",
    Cell: ({ value }: Cell<ReservationListModel>) => {
      return (
        <div className="d-flex flex-column">
          <PhoneNumberColumn phoneNumber={value ? value : "-"} />
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => (item.organizer?.phoneNumber ? formatPhoneNumber(item.organizer.phoneNumber) : "-"),
  },
  {
    Header: "주최자 닉네임",
    accessor: "organizer.displayName",
    width: 120,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      // 닉네임이 길어 말줄임 처리되는 경우에도 마우스 오버 시 전체 닉네임 확인 가능하도록 툴팁 적용
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value || "-"} />
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => item.organizer?.displayName || "-",
  },
  {
    Header: "주최자 이메일",
    accessor: "organizer.userEmail",
    width: 120,
    Cell: ({ value }: Cell<ReservationListModel>) => {
      return <BaseTooltip contents={value || "-"} isSingleLine={true} />;
    },
    excelValue: (item: ReservationListModel) => item.organizer?.userEmail || "-",
  },

  {
    Header: "생성일시",
    accessor: "createdDate",
    width: 160,
    align: "center",
    Cell: (props: Cell<ReservationListModel>) => {
      // 날짜+시간 항상 노출(ISO 8601 표준), 기존 호버 제거
      const createdDate = props.row.original.createdDate;
      return (
        <div>
          <span>{createdDate ? moment(createdDate).format(ViewYmdFormat.FULL) : "-"}</span>
        </div>
      );
    },
    excelValue: (item: ReservationListModel) => (item.createdDate ? moment(item.createdDate).format(ViewYmdFormat.FULL) : "-"),
  },
];

export default ReservationColumns;
